import { motion } from "framer-motion";
import { useState } from "react";
import { Plus, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { panelPill } from "@/lib/panel-ui";

interface EmptyInterestListProps {
  onCreateClick?: () => void;
  onHowItWorksClick?: () => void;
  title?: string;
  subtitle?: string;
  buttonText?: string;
}

export function EmptyInterestList({
  onCreateClick,
  onHowItWorksClick,
  title = "Nenhuma lista de interesse criada",
  subtitle = "Crie sua primeira lista para começar a captar contatos interessados no seu evento.",
  buttonText = "Criar Primeira Lista",
}: EmptyInterestListProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="w-full flex flex-col items-center justify-center py-10 sm:py-14 px-4">
      {/* Pasta 3D Interativa */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="w-72 sm:w-80 h-48 sm:h-52 relative group cursor-pointer mb-8 select-none"
        title="Clique ou passe o mouse para abrir"
      >
        <div
          className="folder-back relative w-[87.5%] mx-auto h-full flex justify-center rounded-2xl overflow-visible bg-secondary/70 dark:bg-muted/50 border border-border/70 shadow-sm"
        >
          {[
            {
              initial: { rotate: -3, x: -36, y: 2 },
              open: { rotate: -8, x: -68, y: -72 },
              transition: {
                type: "spring" as const,
                bounce: 0.15,
                stiffness: 160,
                damping: 22,
              },
              className: "z-10",
            },
            {
              initial: { rotate: 0, x: 0, y: 0 },
              open: { rotate: 1, x: 2, y: -90 },
              transition: {
                type: "spring" as const,
                duration: 0.55,
                bounce: 0.12,
                stiffness: 190,
                damping: 24,
              },
              className: "z-20",
            },
            {
              initial: { rotate: 3.5, x: 40, y: 1 },
              open: { rotate: 9, x: 72, y: -76 },
              transition: {
                type: "spring" as const,
                duration: 0.58,
                bounce: 0.17,
                stiffness: 170,
                damping: 21,
              },
              className: "z-10",
            },
          ].map((page, i) => (
            <motion.div
              key={i}
              initial={page.initial}
              animate={isOpen ? page.open : page.initial}
              transition={page.transition}
              className={`absolute top-2 w-28 sm:w-32 h-fit rounded-xl shadow-md ${page.className}`}
            >
              <Page />
            </motion.div>
          ))}
        </div>

        {/* Frente da Pasta com Rotação 3D */}
        <motion.div
          animate={{ rotateX: isOpen ? -36 : 0 }}
          transition={{ type: "spring", duration: 0.5, bounce: 0.2 }}
          className="absolute inset-x-0 -bottom-px z-30 h-40 sm:h-44 rounded-3xl origin-bottom flex justify-center items-center overflow-visible"
          style={{ transformStyle: "preserve-3d" }}
        >
          <div className="relative w-full h-full">
            <svg
              className="w-full h-full overflow-visible text-secondary/90 dark:text-secondary/70 fill-current stroke-border/70"
              viewBox="0 0 235 121"
              fill="none"
              preserveAspectRatio="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M104.615 0.350494L33.1297 0.838776C32.7542 0.841362 32.3825 0.881463 32.032 0.918854C31.6754 0.956907 31.3392 0.992086 31.0057 0.992096H31.0047C30.6871 0.99235 30.3673 0.962051 30.0272 0.929596C29.6927 0.897686 29.3384 0.863802 28.9803 0.866119L13.2693 0.967682H13.2527L13.2352 0.969635C13.1239 0.981406 13.0121 0.986674 12.9002 0.986237H9.91388C8.33299 0.958599 6.76052 1.22345 5.27423 1.76651H5.27325C4.33579 2.11246 3.48761 2.66213 2.7879 3.37393L2.49689 3.68839L2.492 3.69424C1.62667 4.73882 1.00023 5.96217 0.656067 7.27725C0.653324 7.28773 0.654065 7.29886 0.652161 7.30948C0.3098 8.62705 0.257231 10.0048 0.499817 11.3446L12.2147 114.399L12.2156 114.411L12.2176 114.423C12.6046 116.568 13.7287 118.508 15.3934 119.902C17.058 121.297 19.1572 122.056 21.3231 122.049V122.05H215.379C217.76 122.02 220.064 121.192 221.926 119.698V119.697C223.657 118.384 224.857 116.485 225.305 114.35L225.307 114.339L235.914 53.3798L235.968 53.1093L235.97 53.0985L235.971 53.0888C236.134 51.8978 236.044 50.685 235.705 49.5321C235.307 48.1669 234.63 46.9005 233.717 45.8144L233.383 45.4296C232.58 44.5553 231.614 43.8449 230.539 43.3398C229.311 42.7628 227.971 42.4685 226.616 42.4774H146.746C144.063 42.4705 141.423 41.8004 139.056 40.5263C136.691 39.2522 134.671 37.4127 133.175 35.1689L113.548 5.05948L113.544 5.05362L113.539 5.04776C112.545 3.65165 111.238 2.51062 109.722 1.72061C108.266 0.886502 106.627 0.422235 104.952 0.365143V0.364166L104.633 0.350494H104.615Z"
                strokeWidth="1.5"
              />
            </svg>

            {/* Carinha Simpática na Pasta */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pt-8 pointer-events-none">
              <div className="flex gap-10 sm:gap-11 mb-2.5">
                <div className="w-2.5 h-2.5 bg-foreground/40 dark:bg-foreground/50 rounded-full" />
                <div className="w-2.5 h-2.5 bg-foreground/40 dark:bg-foreground/50 rounded-full" />
              </div>
              <div className="w-8 sm:w-9 h-1 bg-foreground/40 dark:bg-foreground/50 rounded-full" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Textos & Botão de Criação */}
      <div className="text-center space-y-4 max-w-md mx-auto">
        <div>
          <h3 className="font-display text-lg sm:text-xl font-semibold text-foreground tracking-tight">
            {title}
          </h3>
          <p className="mt-1.5 text-xs sm:text-sm text-muted-foreground leading-relaxed">
            {subtitle}
          </p>
        </div>

        <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
          {onCreateClick && (
            <Button
              onClick={onCreateClick}
              className={cn(
                panelPill,
                "group flex items-center gap-2.5 px-6 py-3 h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-md active:scale-[0.97] text-xs sm:text-sm font-semibold w-full sm:w-auto justify-center"
              )}
            >
              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-primary-foreground group-hover:scale-110 transition-transform">
                <Plus className="h-4 w-4" />
              </div>
              <span>{buttonText}</span>
            </Button>
          )}
          {onHowItWorksClick && (
            <Button
              variant="outline"
              onClick={onHowItWorksClick}
              className={cn(
                panelPill,
                "flex items-center gap-2 px-5 py-3 h-12 rounded-full border-border/80 bg-background hover:bg-secondary/60 text-xs sm:text-sm font-medium transition-all active:scale-[0.97] w-full sm:w-auto justify-center text-foreground"
              )}
            >
              <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Play className="ml-0.5 h-2.5 w-2.5 fill-current" />
              </div>
              <span>Ver como funciona</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const Page = () => (
  <div className="w-full h-full bg-card rounded-xl border border-border/70 p-3.5 sm:p-4 shadow-sm">
    <div className="flex flex-col gap-2">
      <div className="w-full h-1.5 bg-primary/30 rounded-full" />
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="flex gap-1.5">
          <div className="flex-1 h-1.5 bg-muted-foreground/20 rounded-full" />
          <div className="flex-1 h-1.5 bg-muted-foreground/10 rounded-full" />
        </div>
      ))}
    </div>
  </div>
);

export default EmptyInterestList;
